<template>
    <div class="form_group form_group_trust_party">
        <display-message :option="message_option"></display-message>

        <!-- AML × ordering integration: per-class picker. Visible when
             the firm has an active AML subscription AND this party class
             is AML-enabled in config/aml.php (otherwise picker endpoint
             returns 422 and aml_picker_enabled stays false). Picking a
             client prefills + locks identity-verified fields. See
             docs/aml-ordering-integration-brief.md MVP § "The party pickers". -->
        <div class="form_row" v-show="aml_picker_enabled && aml_clients.length && !aml_order_exempt">
            <label :for="trust_party_name+'_aml_client'" v-html="cache_data_label.aml_client_id" v-show="cache_data.aml_client_id"></label>
            <div class="select_container">
                <select :id="trust_party_name+'_aml_client'" v-model="cache_data.aml_client_id" @change="fill_form_from_aml($event)">
                    <option value="">— Fill from AML-verified client —</option>
                    <option :value="aml_client_item.id" v-for="aml_client_item in aml_clients">{{ format_aml_client_option(aml_client_item) }}</option>
                </select>
            </div>
            <!-- AML-verified pill — same shape as the customer picker
                 pill on the parent order form. Renders once a client
                 is picked so the user can see at a glance that the
                 party's identity fields below come from a verified
                 source. -->
            <div v-if="aml_selected_client" class="aml_party_pill" :class="aml_selected_client.cdd_status === 'Overdue' ? 'aml_party_pill_warn' : 'aml_party_pill_clear'">
                <span class="aml_party_pill_icon">{{ aml_selected_client.cdd_status === 'Overdue' ? '⏳' : '✓' }}</span>
                <span class="aml_party_pill_label">{{ aml_selected_client.cdd_status === 'Overdue' ? 'Periodic review due' : 'AML-verified' }}</span>
                <span class="aml_party_pill_date" v-if="aml_selected_client.last_review_date">· last reviewed {{ aml_selected_client.last_review_date }}</span>
            </div>
        </div>

        <!-- Verified-required role: free-text is forbidden, so prompt the
             user to pick a cleared AmlClient. Shown until a client is
             selected. See docs/aml-non-subscriber-ordering-brief.md A3. -->
        <!-- Non-subscriber risk disclosure (Part B extension, 2026-07-08):
             CDD-relevant roles must carry an ML/TF risk rating assessed by
             the ordering firm. Mirrors the End Client panel's rating. -->
        <div class="form_row" v-show="party_risk_collect">
            <label :for="trust_party_name+'_risk_rating'">{{ cache_data_label.risk_rating }} <span style="color:#b91c1c;">*</span></label>
            <p style="font-size:0.8em;color:#666;margin:0 0 0.5em;line-height:1.4;">Your firm's money-laundering / terrorism-financing risk assessment of this person or entity.</p>
            <div class="select_container">
                <select :id="trust_party_name+'_risk_rating'" v-model="cache_data.risk_rating">
                    <option value="" disabled>— Select risk rating —</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                </select>
            </div>
        </div>

        <!-- Reliance KYC upload (Allen 2026-07-08): a copy of the firm's
             KYC report/document for each rated party, collected for MGS's
             records under the Reliance Agreement. Staged immediately via
             /client/order_kyc_upload; tokens ride in the party row and are
             linked to it at order save. -->
        <div class="form_row" v-show="party_risk_collect && !cache_data.id">
            <label>KYC report / document <span style="color:#b91c1c;">*</span></label>
            <p style="font-size:0.8em;color:#666;margin:0 0 0.5em;line-height:1.4;">Attach a copy of your firm's KYC verification for this party (PDF, JPG or PNG — up to 5 files, 10MB each). Collected under the Reliance Agreement.</p>
            <!-- Same person in multiple roles (Allen 2026-07-09): reference
                 documents already attached elsewhere on THIS order instead
                 of re-uploading. Backend validates the reference and
                 duplicates the records for this party at save. -->
            <div class="form_row_checkbox_container" v-show="kyc_share_candidates.length" style="margin-bottom:0.5em;">
                <label style="font-size:0.9em;font-weight:normal;">Same person as another party on this order — their KYC documents are attached there</label>
                <input type="checkbox" v-model="kyc_share_ticked">
            </div>
            <div class="select_container" v-show="kyc_share_ticked" style="margin-bottom:0.5em;">
                <select v-model="cache_data.kyc_same_as">
                    <option value="" disabled>— Use the documents attached to —</option>
                    <option v-for="cand in kyc_share_candidates" :key="cand.value" :value="cand.value">{{ cand.label }}</option>
                </select>
            </div>
            <template v-if="!kyc_share_ticked">
                <div v-for="doc in cache_data.kyc_documents" :key="doc.token" style="display:flex;align-items:center;gap:0.6em;font-size:0.85em;margin-bottom:0.3em;">
                    <span class="glyphicon glyphicon-file" style="color:#666;"></span>
                    <span>{{ doc.original_name }}</span>
                    <a href="javascript:void(0);" @click="remove_kyc_file(doc)" title="Remove"><span class="glyphicon glyphicon-trash"></span></a>
                </div>
                <input type="file" :id="trust_party_name+'_kyc_file'" accept=".pdf,.jpg,.jpeg,.png" multiple
                       @change="upload_kyc_files($event)" :disabled="kyc_uploading || (cache_data.kyc_documents && cache_data.kyc_documents.length >= 5)">
                <span v-show="kyc_uploading" style="font-size:0.85em;color:#666;">Uploading…</span>
            </template>
        </div>

        <div class="form_row aml_required_hint" v-show="aml_must_pick">
            <p>
                This role must be filled from an <strong>AML-verified client</strong>.
                <template v-if="aml_picker_enabled && aml_clients.length">Select one from the dropdown above.</template>
                <template v-else>No eligible verified clients were found — onboard them in the AML platform first, then return to this order.</template>
            </p>
        </div>

        <div class="form_row" v-show="option.stored_party&&available_stored_parties.length && !aml_fields_locked">
            <div class="select_container">
                <select :id="trust_party_name+'_stored_party'" @change="fill_form_from_stored($event)">
                    <option value="0">-- Fill from Stored Contact --</option>
                    <option :value="stored_party_item.id" v-for="stored_party_item in available_stored_parties">{{ stored_party_item.formatted_name }}</option>
                </select>
            </div>
        </div>

        <div class="form_row" v-show="available_cached_parties.length && !aml_fields_locked">
            <div class="select_container">
                <select :id="trust_party_name+'_cached_party'" @change="fill_form_from_cached($event)">
                    <option value="0">-- Previously used details --</option>
                    <option :value="cached_party_item.id" v-for="cached_party_item in available_cached_parties">{{ cached_party_item.formatted_name }}</option>
                </select>
            </div>
        </div>

        <div class="form_row" v-show="available_types.length > 1">
            <label :for="trust_party_name+'_type'" v-html="cache_data_label.type" v-show="cache_data.type"></label>
            <div class="form_static_value" v-show="current_trust_party.id || aml_fields_locked" v-html="cache_data.type"></div>
            <div class="select_container" v-show="!current_trust_party.id && !aml_fields_locked">
                <select :id="trust_party_name+'_type'" :placeholder="cache_data_label.type" v-model="cache_data.type">
                    <option value="" disabled v-show="!cache_data.type">-- {{ cache_data_label.type }} --</option>
                    <option :value="available_type" v-for="available_type in available_types">{{ available_type }}</option>
                </select>
            </div>
        </div>

        <div v-if="cache_data.type == 'Individual'">
            <div class="form_row">
                <label :for="trust_party_name+'_title'" v-html="cache_data_label.title" v-show="cache_data.title"></label>
                <div class="select_container">
                    <select :id="trust_party_name+'_title'" :placeholder="cache_data_label.title" v-model="cache_data.title" :disabled="aml_fields_locked" :title="aml_source_tooltip">
                        <option value="" disabled v-show="!cache_data.title">-- {{ cache_data_label.title }} --</option>
                        <option :value="available_title" v-for="available_title in available_titles">{{ available_title }}</option>
                    </select>
                </div>
            </div>
            <div class="form_row">
                <label :for="trust_party_name+'_first_name'" v-html="cache_data_label.first_name" v-show="cache_data.first_name"></label>
                <input type="text" :id="trust_party_name+'_first_name'" :placeholder="cache_data_label.first_name" v-model="cache_data.first_name" :readonly="aml_fields_locked" :title="aml_source_tooltip">
            </div>
            <div class="form_row">
                <label :for="trust_party_name+'_middle_name'" v-html="cache_data_label.middle_name" v-show="cache_data.middle_name"></label>
                <input type="text" :id="trust_party_name+'_middle_name'" :placeholder="cache_data_label.middle_name" v-model="cache_data.middle_name" :readonly="aml_fields_locked" :title="aml_source_tooltip">
            </div>
            <div class="form_row">
                <label :for="trust_party_name+'_last_name'" v-html="cache_data_label.last_name" v-show="cache_data.last_name"></label>
                <input type="text" :id="trust_party_name+'_last_name'" :placeholder="cache_data_label.last_name" v-model="cache_data.last_name" :readonly="aml_fields_locked" :title="aml_source_tooltip">
            </div>
            <div class="form_row form_row_date_container" v-if="(trust_party_class === 'member' || trust_party_class === 'additional_trustee') && !is_smsfu_update">
                <label for="trust_birthday" v-html="cache_data_label.birthday"></label>
                <input type="date" id="trust_birthday" :placeholder="cache_data_label.birthday" v-model="cache_data.birthday" :readonly="aml_fields_locked" :title="aml_source_tooltip">
            </div>
        </div>
        <div v-else-if="cache_data.type == 'Company'">
            <!-- contactType is 'Company'/'Other Entity'-->
            <p style="font-size: 0.8em;padding: 0 1.5em 1em;margin: 0;">Please ensure you enter the full name of the company. E.g. " XYZ Pty Ltd"</p>
            <div class="form_row">
                <label :for="trust_party_name+'_name'" v-html="cache_data_label.name" v-show="cache_data.name"></label>
                <input type="text" :id="trust_party_name+'_name'" :placeholder="cache_data_label.name" v-model="cache_data.name" :readonly="aml_fields_locked" :title="aml_source_tooltip">
            </div>
            <div class="form_row">
                <label :for="trust_party_name+'_company_number'" v-html="cache_data_label.company_number" v-show="cache_data.company_number"></label>
                <input type="text" :id="trust_party_name+'_company_number'" :placeholder="cache_data_label.company_number" v-model="cache_data.company_number" :readonly="aml_fields_locked" :title="aml_source_tooltip">
            </div>
            <p style="font-size: 0.8em;padding: 0 1.5em 1em;margin: 0;">And / Or</p>
            <div class="form_row">
                <label :for="trust_party_name+'_business_number'" v-html="cache_data_label.business_number" v-show="cache_data.business_number"></label>
                <input type="text" :id="trust_party_name+'_business_number'" :placeholder="cache_data_label.business_number" v-model="cache_data.business_number" :readonly="aml_fields_locked" :title="aml_source_tooltip">
            </div>
            <p style="font-size: 0.8em;padding: 0 1.5em 1em;margin: 0;" v-show="trust_party_class != 'company_trustee'">Director name(s) for signature. If there are multiple directors, separate names by comma. e.g. "John Smith, Jane Doe"</p>
            <div class="form_row" v-show="trust_party_class != 'company_trustee'">
                <label :for="trust_party_name+'_director_name'" v-html="cache_data_label.director_name" v-show="cache_data.director_name"></label>
                <input type="text" :id="trust_party_name+'_director_name'" :placeholder="cache_data_label.director_name" v-model="cache_data.director_name">
            </div>
        </div>
        <div v-else-if="cache_data.type == 'Trust'">
            <p style="font-size: 0.8em;padding: 0 1.5em 1em;margin: 0;">Please ensure you enter the full name of the trust. E.g. "The XYZ Trust"</p>
            <div class="form_row">
                <label :for="trust_party_name+'_name'" v-html="cache_data_label.name" v-show="cache_data.name"></label>
                <input type="text" :id="trust_party_name+'_name'" :placeholder="cache_data_label.name" v-model="cache_data.name">
            </div>
            <div class="form_group form_group_trust_party_list">
                <div class="form_row">
                    <label>{{ cache_data_label['trustee'] }}</label>
                </div>
                <div class="form_row form_row_controller_container">
                    <input type="button" class="btn btn-primary" value="Add New" @click="current_trustee = 'new'" style="font-size: 0.6em;" v-show="!current_trustee">
                </div>
                <div class="form_row form_row_list_container">
                    <div class="form_row_list" v-show="!current_trustee" v-for="trust_party_id in cache_data['trustee']">
                        <div>
                            <span class="form_row_list_name">{{ item_map.trust_party[trust_party_id].formatted_name }}</span>
                            <a class="form_row_list_controller" href="javascript:void(0);" @click="current_trustee = trust_party_id"><span class="glyphicon glyphicon-pencil"></span></a>
                            <a class="form_row_list_controller" href="javascript:void(0);" @click="remove_from_list(trust_party_id,cache_data['trustee'])"><span class="glyphicon glyphicon-trash"></span></a>
                        </div>
                    </div>
                </div>
                <update-trust-party v-show="current_trustee" :current_id="current_trustee" @submit="update_trustee" :option="{item_list:item_list,item_map:item_map,trust_party_name:'unit_holder_trustee',trust_party_class:'unit_holder_trustee',trust:cache_data,stored_party:available_stored_parties,aml_subscription_active:option.aml_subscription_active,aml_ordering_exempt:option.aml_ordering_exempt}"></update-trust-party>
            </div>
        </div>

        <div class="form_row" v-if="cache_data.type !== 'Trust'">
            <label :for="trust_party_name+'_address'" v-html="cache_data_label.address_id" v-show="cache_data.address_id"></label>
            <p class="highlight_text" style="font-style: italic;font-size: 0.75em;" v-show="trust_party_class == 'contact'">For PO Boxes, fill details in "Street Name". Leave "Care Of" and "Street Number" blank</p>
            <div class="select_container" v-show="cache_data.address_id !== 'Add New'">
                <select :id="trust_party_name+'_address'" v-model="cache_data.address_id" :placeholder="cache_data_label.address_id" :disabled="aml_fields_locked && !cache_data.aml_prefilled" :title="aml_source_tooltip">
                    <option value="" disabled v-show="!cache_data.address_id">-- {{ cache_data_label.address_id }} --</option>
                    <option value="0" v-show="trust_party_class === 'stored_party'">Empty Address</option>
                    <option :value="cached_address.id" v-for="cached_address in item_list.address">{{ cached_address.full_address }}</option>
                    <option value="Add New" v-if="!aml_fields_locked">Add New</option>
                </select>
            </div>
        </div>
        <update-address :option="{item_list:item_list,item_map:item_map,address_name:trust_party_name+'_address'}" v-model:new_address="cache_data.address_id" v-show="cache_data.address_id == 'Add New'"></update-address>

        <!-- TODO: Input for specific classes -->

        <div class="form_row" v-show="trust_party_class == 'unit_holder' || trust_party_class == 'special_unit_holder'">
            <label :for="trust_party_name+'_unit_count'" v-html="cache_data_label.unit_count" v-show="cache_data.unit_count"></label>
            <input type="text" :id="trust_party_name+'_unit_count'" :placeholder="cache_data_label.unit_count" v-model="cache_data.unit_count" @change="cache_data.unit_count = $root.fix_format(cache_data.unit_count,[{type:'integer'}])">
        </div>
        <div class="form_row" v-show="trust_party_class == 'special_unit_holder' && !option.trust.ng">
            <label :for="trust_party_name+'_income_type'" v-html="cache_data_label.income_type" v-show="cache_data.income_type"></label>
            <div class="select_container">
                <select :id="trust_party_name+'_income_type'" :placeholder="cache_data_label.income_type" v-model="cache_data.income_type">
                    <option value="" v-show="!cache_data.income_type">-- {{ cache_data_label.income_type }} --</option>
                    <option :value="available_income_type" v-for="available_income_type in available_income_types">{{ available_income_type }}</option>
                </select>
            </div>
        </div>
        <div class="form_row" v-show="trust_party_class == 'special_unit_holder' && !option.trust.ng">
            <label :for="trust_party_name+'_redemption_type'" v-html="cache_data_label.redemption_type" v-show="cache_data.redemption_type"></label>
            <div class="select_container">
                <select :id="trust_party_name+'_redemption_type'" :placeholder="cache_data_label.redemption_type" v-model="cache_data.redemption_type">
                    <option value="" v-show="!cache_data.redemption_type">-- {{ cache_data_label.redemption_type }} --</option>
                    <option :value="available_redemption_type" v-for="available_redemption_type in available_redemption_types">{{ available_redemption_type }}</option>
                </select>
            </div>
        </div>
        <div class="form_row" v-show="trust_party_class == 'special_unit_holder' && !option.trust.ng && cache_data.redemption_type.indexOf('Percentage')>-1">
            <label :for="trust_party_name+'_redemption_percentage'" v-html="cache_data_label.redemption_percentage"></label>
            <input type="number" min="0" max="100" step="5" style="width: auto" :id="trust_party_name+'_redemption_percentage'" name="redemption_percentage" v-model="cache_data.redemption_percentage" :placeholder="cache_data_label.redemption_percentage">
            <label :for="trust_party_name+'_redemption_percentage'">%</label>
        </div>
<!--        <div class="form_row" v-show="trust_party_class == 'excluded_class'">-->
<!--            <label :for="trust_party_name+'_description'" v-html="cache_data_label.description" v-show="cache_data.description"></label>-->
<!--            <textarea :id="trust_party_name+'_description'" :placeholder="cache_data_label.description" v-model="cache_data.description"></textarea>-->
<!--        </div>-->

        <div v-if="trust_party_class == 'contact'">
            <div class="form_row">
                <label :for="trust_party_name+'_name'" v-show="cache_data.name">Company Name</label>
                <input type="text" :id="trust_party_name+'_name'" placeholder="Company Name" v-model="cache_data.name">
            </div>
            <div class="form_row">
                <label :for="trust_party_name+'_email'" v-html="cache_data_label.email" v-show="cache_data.email"></label>
                <input type="text" :id="trust_party_name+'_email'" :placeholder="cache_data_label.email" v-model="cache_data.email">
            </div>
            <div class="form_row">
                <label :for="trust_party_name+'_phone'" v-html="cache_data_label.phone" v-show="cache_data.phone"></label>
                <input type="text" :id="trust_party_name+'_phone'" :placeholder="cache_data_label.phone" v-model="cache_data.phone">
            </div>
            <!--            <div class="form_row">-->
            <!--                <label :for="trust_party_name+'_fax'" v-html="cache_data_label.fax" v-show="cache_data.fax"></label>-->
            <!--                <input type="text" :id="trust_party_name+'_fax'" :placeholder="cache_data_label.fax" v-model="cache_data.fax">-->
            <!--            </div>-->
            <div class="form_row form_row_checkbox_container">
                <label :for="trust_party_name+'_update_address'" v-html="cache_data_label.update_address"></label>
                <input type="checkbox" :id="trust_party_name+'_update_address'" name="update_address" v-model="cache_data.update_address">
            </div>
        </div>

        <!-- "Store for Future" checkbox is hidden entirely for firms
             with an active AML subscription — the AML pool replaces
             the legacy stored-party list as the source of truth for
             re-usable party records. The aml_subscription_active flag
             is threaded down via the option prop from the parent
             order form (OrderTrust / OrderSuper). -->
        <div class="form_row form_row_checkbox_container" v-show="!option.aml_subscription_active && cache_data.type !== 'Trust' && trust_party_class !== 'stored_party' && trust_party_class !== 'contact'">
            <label :for="trust_party_name+'_store_for_future'" v-html="cache_data_label.store_for_future"></label>
            <input type="checkbox" :id="trust_party_name+'_store_for_future'" name="store_for_future" v-model="cache_data.store_for_future">
        </div>

        <!-- Todo: Add Field for director_name, multiple Input fields, free text -->

        <div class="form_row form_row_controller_container" v-show="cache_data.address_id != 'Add New'">
            <input type="button" class="btn btn-primary" value="Save" @click="save()">
            <input type="button" class="btn btn-secondary-inverse" value="Cancel" @click="cancel()">
        </div>
    </div>    
</template>

<script>
    import amlApi from '../../services/amlApi.js';

    export default {
        name: "UpdateTrustParty",
        props: ['option','current_id'],
        data() {
            let data_set = {
                map_name: 'trust_party',
                autocomplete: null,
                available_titles: ['Mr','Miss','Mrs','Ms','Mstr','Dr'],
                available_income_types: ['Attributable Income','All the Net Income'],
                available_redemption_types: ['Return of Capital','Return of Capital + CPI','Return of Capital + Percentage','Return of Capital + Annual Percentage','Market Value'],
                // AML × ordering integration: state for the per-class
                // AML-verified-client picker. Populated on mount via the
                // /aml/clients/picker endpoint with order_type=trust and
                // the current party_class. See
                // docs/aml-ordering-integration-brief.md MVP § "The party pickers".
                aml_picker_enabled: false,
                // Verified-required role (config/aml.php ordering_picker
                // {order_type}.{class}.required). When true for a
                // subscriber, free-text is forbidden — the party must be
                // filled from a cleared AmlClient. Set from the picker
                // response meta. See
                // docs/aml-non-subscriber-ordering-brief.md Part A3.
                aml_required: false,
                // Non-subscriber Part B extension (2026-07-08): this
                // CDD-relevant class (same set as the subscriber
                // verified-required classes) must disclose an ML/TF risk
                // rating for the party. Set from the picker response meta —
                // the server only ever sends it for non-subscriber firms.
                party_risk_required: false,
                // Reliance KYC upload in flight (disables the file input).
                kyc_uploading: false,
                // "Same person as another party" checkbox UI state (the
                // chosen source itself lives on cache_data.kyc_same_as).
                kyc_share_ui: false,
                aml_clients: [],
                cache_data_label: {
                    // class: 'Class',             // Define which type of trust party, some may have different hidden fields. e.g. trustee, settlor, unit_holders
                    risk_rating: 'ML/TF risk rating',
                    type: 'Type',               // Individual or Company, individual defined by title, first_name, middle_name and last_name; company defined by name, acn and abn
                    address_id: 'Address',      //
                    trust_id: 'Trust',          // None modifiable field, refer to parent Trust Name

                    // Fields below for type = 'Individual'
                    title: 'Title',
                    first_name: 'First Name',
                    middle_name: 'Middle Name',
                    last_name: 'Last Name',
                    birthday: 'Date of Birth',

                    // Fields below for type = 'Company'
                    name:'Name',
                    company_number:'A.C.N.',
                    business_number:'A.B.N.',

                    // Fields below for type = 'Trust'
                    trustee:'Trustees',

                    // Fields below for special classes
                    email:'Email',                  // Only available for contact, is required if request electronic version of Trust
                    phone:'Phone',                  // Only available for contact
                    fax:'Fax',                      // Only available for contact
                    director_name:'Director Name',    // Only available for trustee that has type as Company, separate multiple names by comma, for sign purpose
                    description:'Description',      // Only available for exclude_class
                    unit_count:'Unit Count',        // Available for unit_holder and special_unit_holder
                    income_type:'Income Type',
                    redemption_type:'Redemption Type',  // Only available for special_unit_holder and is not available when Trust type is HDT
                    redemption_percentage:'Percentage',

                    update_address: 'Save delivery address',
                    store_for_future: 'Store for Future',
                    aml_client_id: 'AML-verified Client',
                },
                current_trustee: '',
                message_option: {
                    message: '',
                    message_type: ''
                },
            };
            let current_object = this;
            data_set.cache_data = current_object.reset_cache_data({init:true});
            Object.keys(current_object.option).forEach(function (option_key) {
                data_set[option_key] = current_object.option[option_key];
            });
            return data_set;
        },
        computed: {
            // True when this party belongs to an SMSF *Update* (SMSFU) order.
            // Date of Birth is only collected for newly-established funds
            // (SMSF), not when updating an existing one — so the DOB row is
            // hidden and dropped from validation for SMSFU. super_type is
            // passed down from OrderSuper.vue's cache_data.type.
            is_smsfu_update: function () {
                return !!(this.option && this.option.super_type === 'SMSFU');
            },
            available_stored_parties: function () {
                let current_object = this;
                let result = [];
                if (current_object.option.stored_party) {
                    let available_types = current_object.available_types;
                    current_object.option.stored_party.forEach(function (stored_party_item) {
                        if (available_types.indexOf(stored_party_item.type) !== -1) {
                            result.push(stored_party_item);
                        }
                    });
                }
                return result;
            },
            available_cached_parties: function () {
                let current_object = this;
                let result = {};
                if (current_object.item_list[current_object.map_name]) {
                    let available_types = current_object.available_types;
                    current_object.item_list[current_object.map_name].forEach(function (party_item) {
                        if (available_types.indexOf(party_item.type) !== -1) {
                            result[party_item.formatted_name] = party_item;
                        }
                    });
                }
                return Object.values(result);
            },
            available_types: function () {
                let current_object = this;
                let result = ['Individual'];

                // -- Except trust_party_class listed below, trust_party can have type Individual or Company

                // 25 Mar, 2024, Michael called Chris to confirm that appointor can be a Company
                if (['contact','beneficiary','beneficiary_a','beneficiary_b','beneficiary_cv','beneficiary_grandparent','beneficiary_parent','beneficiary_child','member','additional_trustee','voter','borrower'].indexOf(current_object.option.trust_party_class) === -1) {
                // if (['contact','appointor','beneficiary','beneficiary_a','beneficiary_b','beneficiary_cv','member','additional_trustee'].indexOf(current_object.option.trust_party_class) === -1) {
                // 28 Mar, 2023 change to only Principal Beneficiary has to be Individual
                // if (['contact','beneficiary'].indexOf(current_object.option.trust_party_class) === -1) {
                    result.push('Company');
                }

                // For trust_party_class listed below, trust_party type is Company only
                if (['company_trustee'].indexOf(current_object.option.trust_party_class) !== -1) {
                    result = ['Company'];
                }


                // For trust_party_class listed below, trust_party can also choose the type Trust
                if (['unit_holder','special_unit_holder','lender'].indexOf(current_object.option.trust_party_class) !== -1) {
                    result.push('Trust');
                }

                return result;
            },
            // AML × ordering: identity-verified fields render readonly /
            // disabled when this party was sourced from a cleared
            // AmlClient. Document-specific fields (proportions,
            // redemption types, etc.) stay editable.
            //
            // MGS admins editing an order from the admin panel always
            // see editable fields — they need to be able to fix typos,
            // tweak details for delivery, etc. without round-tripping
            // through the AML side.
            aml_locked: function () {
                if (this.$root && this.$root.panel === 'admin') {
                    return false;
                }
                return !!(this.cache_data.aml_client_id);
            },
            // The parent order is a product exempt from ALL AML ordering
            // gates (e.g. SMSFU — see config/aml.php ordering_exempt_types).
            // The picker fetch runs at mount, before the user may have
            // picked the product type, so this must be honoured LIVE from
            // the (reactive) option prop rather than by skipping the fetch.
            aml_order_exempt: function () {
                return !!(this.option && this.option.aml_ordering_exempt);
            },
            // Non-subscriber risk disclosure: show the ML/TF rating dropdown
            // when the server flagged this class as CDD-relevant for a
            // non-subscriber firm (party_risk_required only ever arrives for
            // non-subscribers) and the order isn't an exempt product.
            party_risk_collect: function () {
                return this.party_risk_required && ! this.aml_order_exempt;
            },
            // Sources this party can reference instead of re-uploading:
            // sibling parties on the order that carry their OWN uploads
            // (no deferral chains), plus the End Client where the parent
            // order's cache_data is reachable via option.trust.
            kyc_share_candidates: function () {
                let current_object = this;
                let result = [];
                let parent = current_object.option && current_object.option.trust;
                if (parent && Array.isArray(parent.end_client_kyc_documents) && parent.end_client_kyc_documents.length) {
                    result.push({ value: 'end_client', label: 'End Client — ' + (parent.end_client_name || 'the client') });
                }
                let map = (current_object.option && current_object.option.item_map && current_object.option.item_map.trust_party) || {};
                Object.keys(map).forEach(function (id) {
                    let row = map[id];
                    if (! row || String(id) === String(current_object.cache_data.id)) return;
                    if (row.kyc_same_as) return; // no chains
                    if (Array.isArray(row.kyc_documents) && row.kyc_documents.length) {
                        result.push({ value: String(id), label: row.formatted_name || ('Party ' + id) });
                    }
                });
                return result;
            },
            kyc_share_ticked: {
                get: function () { return this.kyc_share_ui || !! this.cache_data.kyc_same_as; },
                set: function (v) {
                    this.kyc_share_ui = v;
                    if (! v) this.cache_data.kyc_same_as = '';
                },
            },
            // Verified-required role still awaiting a pick. The user must
            // choose an AML-verified client before saving; free-text entry
            // is blocked. Admins are exempt — they edit orders directly in
            // the panel. See docs/aml-non-subscriber-ordering-brief.md A3.
            aml_must_pick: function () {
                if (this.aml_order_exempt) {
                    return false;
                }
                if (this.$root && this.$root.panel === 'admin') {
                    return false;
                }
                return !!(this.aml_required && !this.cache_data.aml_client_id);
            },
            // Identity fields are non-editable because a client was picked
            // (aml_locked), because this is a verified-required role still
            // awaiting a pick (aml_must_pick), or because the row was
            // pre-filled from a verified client's records (aml_prefilled —
            // e.g. the nested trustee auto-created when a verified Trust is
            // picked as a unit holder). Drives the readonly/disabled
            // bindings and hides the legacy fill fallbacks. Never locked on
            // AML-exempt orders — they are free-text by definition, and the
            // backend strips any picked-client provenance at submit anyway.
            // NOTE: the Address binding exempts the prefilled case — AP
            // records carry no address, and address is mandatory, so it
            // must stay enterable.
            aml_fields_locked: function () {
                if (this.aml_order_exempt) {
                    return false;
                }
                return this.aml_locked || this.aml_must_pick || !!this.cache_data.aml_prefilled;
            },
            // Hover-tooltip text for locked identity fields. Shows the
            // source AmlClient + last-review date so the user knows where
            // the locked value comes from. Removing the :title bindings
            // below + this computed reverts the tooltip cleanly.
            aml_source_tooltip: function () {
                if (this.cache_data.aml_prefilled && ! this.cache_data.aml_client_id) {
                    return "Pre-filled from the verified client's records — remove and re-add the trustee to enter different details.";
                }
                if (! this.cache_data.aml_client_id) return null;
                let src = (this.aml_clients || []).find(c => c.id === this.cache_data.aml_client_id);
                if (! src) return null;
                let bits = ['Source: ' + src.name];
                if (src.last_review_date) bits.push('last reviewed ' + src.last_review_date);
                if (src.cdd_status === 'Overdue') bits.push('periodic review due');
                return bits.join(' · ');
            },

            // The currently-picked AmlClient object (or null), used by
            // the AML-verified pill rendered next to the picker. Mirrors
            // aml_selected_customer on the parent OrderTrust.vue.
            aml_selected_client: function () {
                if (! this.cache_data.aml_client_id) return null;
                return (this.aml_clients || []).find(c => c.id === this.cache_data.aml_client_id) || null;
            },
        },
        methods: {
            reset_cache_data: function(option) {
                if (typeof option === 'undefined') {
                    option = {};
                }

                let current_object = this;

                let cache_data = {
                    // class: '',
                    type: 'Individual',
                    address_id: '',
                    trust_id: '',
                    aml_client_id: '',
                    risk_rating: '',
                    kyc_documents: [],
                    kyc_same_as: '',

                    // Fields below for type = 'Individual'
                    title: '',
                    first_name: '',
                    middle_name: '',
                    last_name: '',
                    birthday: '',

                    // Fields below for type = 'Company'
                    name: '',
                    company_number: '',
                    business_number: '',

                    // Fields below for type = 'Trust'
                    trustee: [],

                    // Fields below for special classes
                    email:'',
                    phone:'',
                    fax:'',
                    director_name: '',
                    description: '',
                    unit_count: 1,
                    income_type: '',
                    redemption_type: '',
                    redemption_percentage: 0,
                    income_ld_proportion: 1,
                    income_proportion: 1,
                    capital_ld_proportion: 1,
                    capital_proportion: 1,

                    update_address: false,
                    store_for_future: false,
                };
                if (!option.init) {
                    current_object.cache_data = Object.assign(current_object.cache_data,cache_data);
                    cache_data = current_object.cache_data;
                }

                current_object.current_trust_party = {};
                if (current_object.option) {
                    if (['company_trustee','trustee'].indexOf(current_object.option.trust_party_class) !== -1) {
                    // if (current_object.option.trust_party_class === 'trustee') {
                        cache_data.type = 'Company';
                    }
                    // if (['appointor','beneficiary','beneficiary_b'].indexOf(current_object.option.trust_party_class) !== -1) {
                    //     cache_data.type = 'Individual';
                    // }
                    // if (current_object.available_types.length === 1) {
                    //     cache_data.type = current_object.available_types[0];
                    // }
                    if (current_object.current_id) {
                        if (current_object.option.item_map['trust_party'][current_object.current_id]) {
                            cache_data = Object.assign(cache_data, current_object.option.item_map['trust_party'][current_object.current_id]);
                            current_object.current_trust_party = current_object.option.item_map['trust_party'][current_object.current_id];
                            // Coerce null/undefined back to '' so v-model
                            // bindings on <select>s match their disabled
                            // placeholder options (which have value="").
                            // Saved DB rows can carry NULLs that wouldn't
                            // otherwise match anything → empty dropdown
                            // with no placeholder visible.
                            Object.keys(cache_data).forEach(function (key) {
                                if (cache_data[key] === null || typeof cache_data[key] === 'undefined') {
                                    cache_data[key] = '';
                                }
                            });
                            // director_name can arrive as an array
                            // (e.g. ["Michael","Allen","MM"]) but the field
                            // is a free-text input bound via v-model. Flatten
                            // it to a comma-separated string so it renders.
                            if (Array.isArray(cache_data.director_name)) {
                                cache_data.director_name = cache_data.director_name.join(',');
                            }
                        }
                    }
                }


                return cache_data;

            },

            fill_form_from_cached: function(event) {
                // console.log('fill_form_from_stored',event);
                let current_object = this;
                if (current_object.item_map[current_object.map_name] && current_object.item_map[current_object.map_name][event.target.value]) {
                    let cached_party = current_object.item_map[current_object.map_name][event.target.value];
                    Object.keys(current_object.cache_data).forEach(function (field_name) {
                        if (['type','title','first_name','middle_name','last_name','name','company_number','business_number','director_name','address_id'].indexOf(field_name) !== -1) {
                            current_object.cache_data[field_name] = cached_party[field_name];
                        }
                    });
                    // director_name may be stored as an array; the field is a
                    // free-text input, so flatten to a comma-separated string.
                    if (Array.isArray(current_object.cache_data.director_name)) {
                        current_object.cache_data.director_name = current_object.cache_data.director_name.join(',');
                    }
                }
                event.target.value = 0;
            },

            // AML × ordering integration: handler for the "Fill from
            // AML-verified client" dropdown. Mirrors fill_form_from_stored
            // but also stamps cache_data.aml_client_id, which the
            // template uses to lock identity-verified fields and which
            // the server rechecks for status drift at submit.
            // Reliance KYC: stage each selected file immediately via
            // /client/order_kyc_upload; the returned token rides in
            // cache_data.kyc_documents and is linked to the saved party row
            // by the order handler. See PanelController::order_kyc_upload.
            upload_kyc_files: function (event) {
                let current_object = this;
                let files = Array.prototype.slice.call((event && event.target && event.target.files) || []);
                if (event && event.target) event.target.value = '';
                if (! Array.isArray(current_object.cache_data.kyc_documents)) {
                    current_object.cache_data.kyc_documents = [];
                }
                files.forEach(function (f) {
                    if (current_object.cache_data.kyc_documents.length >= 5) return;
                    if (f.size > 10 * 1024 * 1024) {
                        current_object.message_option.message = ['"' + f.name + '" is too large — the limit is 10MB per file.'];
                        current_object.message_option.message_type = 'error';
                        return;
                    }
                    let fd = new FormData();
                    fd.append('file', f);
                    current_object.kyc_uploading = true;
                    axios.post('/client/order_kyc_upload', fd).then(function (res) {
                        let d = res.data || {};
                        if (d.kyc_document) {
                            current_object.cache_data.kyc_documents.push(d.kyc_document);
                        } else if (d.message && d.message.length) {
                            current_object.message_option.message = d.message;
                            current_object.message_option.message_type = 'error';
                        }
                    }).catch(function () {
                        current_object.message_option.message = ['Upload failed — please try again.'];
                        current_object.message_option.message_type = 'error';
                    }).finally(function () {
                        current_object.kyc_uploading = false;
                    });
                });
            },
            remove_kyc_file: function (doc) {
                let current_object = this;
                axios.post('/client/order_kyc_delete', { token: doc.token }).finally(function () {
                    let idx = current_object.cache_data.kyc_documents.indexOf(doc);
                    if (idx > -1) current_object.cache_data.kyc_documents.splice(idx, 1);
                });
            },
            fill_form_from_aml: function (event) {
                let current_object = this;
                let picked_id = event && event.target ? event.target.value : '';

                if (!picked_id || picked_id === '0') {
                    // Cleared — unlock fields, keep current data so the
                    // user can edit further if they want.
                    current_object.cache_data.aml_client_id = '';
                    return;
                }

                let picked = current_object.aml_clients.find(function (c) { return String(c.id) === String(picked_id); });
                if (!picked) {
                    return;
                }

                current_object.cache_data.aml_client_id = picked.id;
                current_object.cache_data.type = picked.client_type;

                // Trust-type slots (unit holders) accept verified Trust OR
                // SMSF clients — both map onto the form's 'Trust' type
                // (an SMSF is a trust for deed purposes; Allen 2026-07-08).
                if (picked.client_type === 'Trust' || picked.client_type === 'SMSF') {
                    current_object.cache_data.type = 'Trust';
                    current_object.cache_data.name = picked.name || '';
                    current_object.cache_data.first_name  = '';
                    current_object.cache_data.middle_name = '';
                    current_object.cache_data.last_name   = '';
                    current_object.cache_data.company_number  = '';
                    current_object.cache_data.business_number = picked.abn || '';

                    // Pre-populate the nested trustee(s) from the verified
                    // client's known trustee APs (2026-07-08). Same row
                    // mechanics as the manual "Add New" flow: register in
                    // item_map/item_list under a fresh new_N id, then link
                    // via cache_data.trustee. Only when the trustee list is
                    // still empty — never clobber manual entries.
                    if (!Array.isArray(current_object.cache_data['trustee'])) {
                        current_object.cache_data['trustee'] = [];
                    }
                    if (Array.isArray(picked.trustee_parties) && picked.trustee_parties.length
                        && !current_object.cache_data['trustee'].length) {
                        picked.trustee_parties.forEach(function (tp) {
                            let isCompany = tp.type === 'Company';
                            let row = {
                                class: 'unit_holder_trustee',
                                // Session-scoped lock marker: identity fields
                                // came from the verified client's records, so
                                // the edit view keeps them readonly (remove +
                                // re-add manually to override). Not in the
                                // model fillable — never persisted.
                                aml_prefilled: 1,
                                type: isCompany ? 'Company' : 'Individual',
                                title: '',
                                first_name: isCompany ? '' : (tp.first_name || ''),
                                middle_name: isCompany ? '' : (tp.middle_name || ''),
                                // Legacy APs may carry only a combined name —
                                // fall back so the trustee is never blank.
                                last_name: isCompany ? '' : (tp.last_name || tp.name || ''),
                                name: isCompany ? (tp.name || '') : '',
                                company_number: tp.acn || '',
                                business_number: tp.abn || '',
                                formatted_name: tp.formatted_name || tp.name || '',
                                parent_id: current_object.current_trust_party.id,
                            };
                            let index_counter = 1;
                            while (current_object.option.item_map['trust_party']['new_' + index_counter]) {
                                index_counter++;
                            }
                            row.id = 'new_' + index_counter;
                            current_object.option.item_map['trust_party'][row.id] = row;
                            if (current_object.item_list['trust_party'].indexOf(row) === -1) {
                                current_object.item_list['trust_party'].push(row);
                            }
                            current_object.cache_data['trustee'].push(row.id);
                        });
                    }
                }

                if (picked.client_type === 'Individual') {
                    current_object.cache_data.first_name  = picked.first_name  || '';
                    current_object.cache_data.middle_name = picked.middle_name || '';
                    current_object.cache_data.last_name   = picked.last_name   || '';
                    current_object.cache_data.birthday    = picked.dob          || '';
                    current_object.cache_data.name        = '';
                    current_object.cache_data.company_number = '';
                    current_object.cache_data.business_number = '';
                } else if (picked.client_type === 'Company') {
                    current_object.cache_data.name             = picked.name || '';
                    current_object.cache_data.company_number   = picked.acn  || '';
                    current_object.cache_data.business_number  = picked.abn  || '';
                    current_object.cache_data.first_name       = '';
                    current_object.cache_data.middle_name      = '';
                    current_object.cache_data.last_name        = '';
                    // Pre-fill director_name from the AmlClient's
                    // active/confirmed director APs (comma-joined).
                    // Only overwrite when we have data so an
                    // already-edited value isn't blown away with a
                    // blank when the corporate AmlClient has no
                    // director APs recorded yet.
                    if (picked.director_names) {
                        current_object.cache_data.director_name = picked.director_names;
                    }
                }

                if (picked.email) current_object.cache_data.email = picked.email;
                if (picked.phone) current_object.cache_data.phone = picked.phone;

                // Reuse the AmlClient's address record. Set address_id
                // even if the full address record didn't come back so
                // validation passes; drop the address into local
                // item_map / item_list when available so the address
                // dropdown can render it visibly.
                if (picked.address_id) {
                    current_object.cache_data.address_id = picked.address_id;
                    if (picked.address && !current_object.item_map.address[picked.address_id]) {
                        current_object.item_map.address[picked.address_id] = picked.address;
                        current_object.item_list.address.push(picked.address);
                    }
                }
            },

            format_aml_client_option: function (opt) {
                let parts = [opt.client_type];
                if (opt.cdd_status === 'Overdue') parts.push('review due');
                if (opt.last_review_date) parts.push('last reviewed ' + opt.last_review_date);
                return opt.name + ' (' + parts.join(' · ') + ')';
            },

            fill_form_from_stored: function(event) {
                // console.log('fill_form_from_stored',event);
                let current_object = this;
                if (current_object.$root.item_map.stored_party && current_object.$root.item_map.stored_party[event.target.value]) {
                    let stored_party = current_object.$root.item_map.stored_party[event.target.value];
                    Object.keys(current_object.cache_data).forEach(function (field_name) {
                        if (['id','description'].indexOf(field_name) !== -1) {
                            return false;
                        }
                        if (typeof stored_party[field_name] !== 'undefined') {
                            current_object.cache_data[field_name] = stored_party[field_name];
                        }
                    });
                }
                if (current_object.cache_data['address_id'] === 0) {
                    current_object.cache_data['address_id'] = '';
                } else {
                    if (!current_object.item_map.address[current_object.cache_data['address_id']] && current_object.$root.item_map.address[current_object.cache_data['address_id']]) {
                        current_object.item_map.address[current_object.cache_data['address_id']] = current_object.$root.item_map.address[current_object.cache_data['address_id']];
                        current_object.item_list.address.push(current_object.item_map.address[current_object.cache_data['address_id']]);
                    }
                }
                event.target.value = 0;
            },

            update_trustee: function(trust_party_id, trust_party_class) {

                let current_object = this;
                let trust_party = current_object.option.item_map.trust_party[trust_party_id];

                current_object.current_trustee = '';
                if (!trust_party_id) {
                    return false;
                }

                if (!trust_party) {
                    current_object.message_option.message = ['Invalide trust_party_id: ',trust_party_id];
                    current_object.message_option.message_type = 'error';
                    return false;
                }
                trust_party.parent_id = current_object.current_trust_party.id;

                if (current_object.cache_data['trustee'].indexOf(trust_party_id) === -1) {
                    current_object.cache_data['trustee'].push(trust_party_id);
                }
            },
            remove_from_list: function (item, list) {
                let index = list.indexOf(item);
                if (index > -1) {
                    list.splice(index,1);
                }
            },

            save: function () {
                // TODO: if store_for_future checked, post stored data to server

                let current_object = this;
                let warning_message = [];
                let mandatory_fields = [];
                let formatted_name = '';
                let post_data = Object.assign({},current_object.cache_data);

                current_object.message_option.message = '';

                // Verified-only ordering (from 1 July 2026): a
                // verified-required role must be filled from an AML-verified
                // client — block the save until one is picked.
                if (current_object.aml_must_pick) {
                    current_object.message_option.message = ['This role must be filled from an AML-verified client — select one from the dropdown above. If they are not listed, onboard them in the AML platform first.'];
                    current_object.message_option.message_type = 'error';
                    return false;
                }

                // Non-subscriber risk disclosure: block the save until a
                // rating is selected. Only for NEW party rows — parties on
                // pre-feature orders can still be edited without one (the
                // backend gate is likewise new-orders-only).
                if (current_object.party_risk_collect
                    && ! current_object.cache_data.id
                    && ['low', 'medium', 'high'].indexOf(current_object.cache_data.risk_rating) === -1) {
                    current_object.message_option.message = ['Select the ML/TF risk rating for this party before saving.'];
                    current_object.message_option.message_type = 'error';
                    return false;
                }

                // Reliance KYC: every rated party must carry at least one
                // uploaded KYC report/document — or reference another
                // source on this order ("same person as"). Backend
                // hard-gates both paths.
                if (current_object.party_risk_collect && ! current_object.cache_data.id) {
                    if (current_object.kyc_share_ticked && ! current_object.cache_data.kyc_same_as) {
                        current_object.message_option.message = ['Choose whose documents to use for this party (the "Use the documents attached to" dropdown).'];
                        current_object.message_option.message_type = 'error';
                        return false;
                    }
                    if (! current_object.cache_data.kyc_same_as
                        && (! Array.isArray(current_object.cache_data.kyc_documents) || ! current_object.cache_data.kyc_documents.length)) {
                        current_object.message_option.message = ["Attach a copy of this party's KYC report/document before saving."];
                        current_object.message_option.message_type = 'error';
                        return false;
                    }
                }

                switch (post_data.type) {
                    case 'Individual':
                        formatted_name = post_data.last_name;
                        if (post_data.middle_name) {
                            formatted_name = post_data.middle_name + ' ' + formatted_name;
                        }
                        if (post_data.first_name) {
                            formatted_name = post_data.first_name + ' ' + formatted_name;
                        }
                        if (post_data.title) {
                            formatted_name = post_data.title + ' ' + formatted_name;
                        }

                        post_data.formatted_name = formatted_name;

                        if (post_data.birth_country !== 'AU') {
                            post_data.birth_state = 'Foreign';
                        }
                        mandatory_fields.push('first_name');
                        mandatory_fields.push('last_name');
                        if (current_object.trust_party_class !== 'stored_party') {
                            mandatory_fields.push('address_id');
                        }
                        if (current_object.trust_party_class === 'member' && !current_object.is_smsfu_update) {
                            mandatory_fields.push('birthday');
                        }

                        if (post_data.director_id) {
                            // Director ID validation, 15 digits start with 036 as Australian country code, last digit for check (not sure about the algorithm yet)
                            let stripped_director_id = post_data.director_id.replace(/[^0-9]+/g, '');
                            if (!stripped_director_id.match(/^036(\d){12}/)) {
                                warning_message.push('Invalid Director ID.');
                            }
                        }
                        break;
                    case 'Trust':
                        let i = 0;
                        let name_stack = [];
                        post_data.formatted_name = '';
                        post_data.address_id = 0;
                        post_data.business_number = '';
                        post_data.company_number = '';
                        // post_data.director_name = '';
                        while (post_data.trustee[i]) {
                            if (i > 0) {
                                if (i == post_data.trustee.length -1) {
                                    post_data.formatted_name += ' and ';
                                } else {
                                    post_data.formatted_name += ', ';
                                }
                            }
                            if (!post_data.address_id) {
                                post_data.address_id = current_object.option.item_map.trust_party[post_data.trustee[i]].address_id;
                            }
                            if (!post_data.business_number) {
                                post_data.business_number = current_object.option.item_map.trust_party[post_data.trustee[i]].business_number;
                            }
                            if (!post_data.company_number) {
                                post_data.company_number = current_object.option.item_map.trust_party[post_data.trustee[i]].company_number;
                            }
                            // if (current_object.option.item_map.trust_party[post_data.trustee[i]].director_name) {
                            //     if (post_data.director_name) {
                            //         post_data.director_name += ',';
                            //     }
                            //     post_data.director_name += current_object.option.item_map.trust_party[post_data.trustee[i]].director_name;
                            // }

                            if (current_object.option.item_map.trust_party[post_data.trustee[i]].type === 'Company') {
                                post_data.formatted_name += current_object.option.item_map.trust_party[post_data.trustee[i]].name;
                            } else {
                                post_data.formatted_name += current_object.option.item_map.trust_party[post_data.trustee[i]].formatted_name;
                            }
                            i++;
                        }
                        post_data.formatted_name += ' as trustee';
                        if (i > 1) {
                            post_data.formatted_name += 's';
                        }
                        post_data.formatted_name += ' for ' + post_data.name;
                        if (i < 1) {
                            warning_message.push('Field "'+current_object.cache_data_label['trustee']+'" is required.');
                        }
                        break;
                    case 'Company':
                    default:
                        post_data.formatted_name = post_data.name;
                        if (post_data.business_number) {
                            post_data.formatted_name += ' (A.B.N. '+post_data.business_number + ')';
                        } else {
                            if (post_data.company_number) {
                                post_data.formatted_name += ' (A.C.N. '+post_data.company_number + ')';
                            } else {
                                warning_message.push('Company and other entities must provide A.B.N. and/or A.C.N. for identification.');
                            }
                        }
                        mandatory_fields.push('name');
                        if (current_object.trust_party_class !== 'stored_party') {
                            mandatory_fields.push('address_id');
                        }

                        break;
                }
                switch (current_object.trust_party_class) {
                    case 'special_unit_holder':
                        mandatory_fields.push('unit_count');
                        if (current_object.option.trust && current_object.option.trust.ng) {
                            post_data.income_type = 'Attributable Income';
                            post_data.redemption_type = 'Negative Gearing';
                            post_data.redemption_percentage = 0;
                        } else {
                            mandatory_fields.push('income_type','redemption_type');
                        }
                        break;
                    case 'unit_holder':
                        mandatory_fields.push('unit_count');
                        break;
                    case 'contact':
                        mandatory_fields.push('email');
                        mandatory_fields.push('phone');
                        break;
                }
                // General Validation, e.g. mandatory field empty, number format wrong...
                mandatory_fields.forEach(function (field_name) {
                    if (!post_data[field_name]) {
                        warning_message.push('Field "'+current_object.cache_data_label[field_name]+'" is required.');
                    } else {
                        if(field_name === 'redemption_type' && post_data[field_name].indexOf('Percentage') > -1) {
                            // When "Redemption Type" is mandatory field and has the "Percentage" related types selected, Redemption Percentage is mandatory
                            if (!post_data['redemption_percentage'] || post_data['redemption_percentage'] == '0') {
                                // v-model will make number input value as string, '0' is not equivalent to empty
                                warning_message.push('Field "'+current_object.cache_data_label['redemption_percentage']+'" is required.');
                            }
                        }
                    }
                });
                if (post_data.email) {
                    if (!post_data.email.toLowerCase()
                        .match(
                            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                        )) {
                        warning_message.push('Invalid Email Address.');
                    }
                }
                // AML × ordering: skip ABN/ACN/TFN format validation when
                // the party was sourced from a verified AmlClient — the
                // identifier already passed Shufti / ABR / ASIC checks
                // during onboarding.
                if (post_data.tax_number && !current_object.aml_locked) {
                    // Director ID validation, 15 digits start with 036 as Australian country code, last digit for check (not sure about the algorithm yet)
                    let stripped_tax_number = post_data.tax_number.replace(/[^0-9]+/g, '');
                    if (stripped_tax_number.length != 9 ) {
                        warning_message.push('Invalid Tax File Number.');
                    } else {
                        let digits = stripped_tax_number.split('');
                        let sum = (digits[0]*1)
                            + (digits[1]*4)
                            + (digits[2]*3)
                            + (digits[3]*7)
                            + (digits[4]*5)
                            + (digits[5]*8)
                            + (digits[6]*6)
                            + (digits[7]*9)
                            + (digits[8]*10);

                        if (sum % 11 != 0) {
                            warning_message.push('Invalid Tax File Number.');
                        }
                    }
                }
                if (post_data.business_number && !current_object.aml_locked) {
                    let stripped_number = post_data.business_number.replace(/[^0-9]+/g, '');
                    if (stripped_number.length !== 11 ) {
                        warning_message.push('Invalid '+current_object.cache_data_label['business_number']+' (11 digit number required)');
                    } else {
                        let digits = stripped_number.split('');
                        let sum = (digits[0]*10)
                            + (digits[1]*1)
                            + (digits[2]*3)
                            + (digits[3]*5)
                            + (digits[4]*7)
                            + (digits[5]*9)
                            + (digits[6]*11)
                            + (digits[7]*13)
                            + (digits[8]*15)
                            + (digits[9]*17)
                            + (digits[10]*19);
console.log('abn sum',sum);

                        if (sum % 89 !== 10) {
                            warning_message.push('Invalid '+current_object.cache_data_label['business_number']);
                        }
                    }
                }
                if (post_data.company_number && !current_object.aml_locked) {
                    let stripped_number = post_data.company_number.replace(/[^0-9]+/g, '');
                    if (stripped_number.length !== 9 ) {
                        warning_message.push('Invalid '+current_object.cache_data_label['company_number']+' (9 digit number required)');
                    } else {
                        let digits = stripped_number.split('');
                        let sum = (digits[0]*8)
                            + (digits[1]*7)
                            + (digits[2]*6)
                            + (digits[3]*5)
                            + (digits[4]*4)
                            + (digits[5]*3)
                            + (digits[6]*2)
                            + (digits[7]*1)
                            + (digits[8]*1);
console.log('acn sum',sum);
                        if (sum % 10 !== 0) {
                            warning_message.push('Invalid '+current_object.cache_data_label['company_number']);
                        }
                    }
                }


                if (warning_message.length > 0) {
                    current_object.message_option.message_type = 'error';
                    current_object.message_option.message = warning_message;
                    return false;
                } else {
                    current_object.message_option.message_type = 'success';
                    current_object.message_option.message = '';
                }

                current_object.current_trust_party.class = current_object.trust_party_class;
                if (!current_object.current_trust_party.id) {
                    current_object.current_trust_party = Object.assign(current_object.current_trust_party,post_data);
                    let index_counter = 1;
                    while (current_object.option.item_map['trust_party']['new_'+index_counter]) {
                        index_counter++;
                    }
                    current_object.current_trust_party.id = 'new_'+index_counter;

                    current_object.option.item_map['trust_party'][current_object.current_trust_party.id] = current_object.current_trust_party;
                    if (current_object.item_list['trust_party'].indexOf(current_object.current_trust_party) === -1) {
                        current_object.item_list['trust_party'].push(current_object.current_trust_party);
                    }
                } else {
                    current_object.current_trust_party = Object.assign(current_object.current_trust_party,post_data);
                }
                // $('html, body').stop().animate({
                //     scrollTop:$(current_object.$el).offset().top - 200
                // }, 550);
                if ($(current_object.$el.parentNode).hasClass('scrolltop_container')) {
                    let scrolltop_wrapper = $(current_object.$el.parentNode).closest('.scrolltop_wrapper');
                    if (scrolltop_wrapper.length > 0) {
                        $('html, body').scrollTop(scrolltop_wrapper.offset().top);
                    }
                }
console.log('allen debug update contact submit',current_object.current_trust_party.id,current_object.trust_party_class,current_object.option.item_map,current_object.item_list);
                // this.$emit('update:new_contact',this.cache_data.id);
                current_object.$emit('submit',current_object.current_trust_party.id,current_object.trust_party_class);
            },
            cancel: function () {
                let current_object = this;

                // Erase error message if applicable
                current_object.message_option.message_type = '';
                current_object.message_option.message = '';

console.log('allen debug update_trust_party cancel',current_object.current_trust_party.id,current_object.trust_party_class,current_object.option.item_map,current_object.item_list);
                // this.$emit('update:new_contact','');
                // $('html, body').stop().animate({
                //     scrollTop:$(current_object.$el).offset().top - 200
                // }, 550);
                if ($(current_object.$el.parentNode).hasClass('scrolltop_container')) {
                    let scrolltop_wrapper = $(current_object.$el.parentNode).closest('.scrolltop_wrapper');
                    if (scrolltop_wrapper.length > 0) {
                        $('html, body').scrollTop(scrolltop_wrapper.offset().top);
                    }
                }
                // current_object.$emit('submit',current_object.current_trust_party.id?current_object.current_trust_party.id:'',current_object.trust_party_class);
                current_object.$emit('submit','',current_object.trust_party_class);
            }
        },
        mounted() {
            let current_object = this;

            // AML × ordering: fetch the cleared AML clients eligible
            // for this party class. The picker endpoint returns 422 for
            // classes that aren't AML-enabled (config aml=false) or 4xx
            // when the firm has no active subscription — both result in
            // the picker staying hidden.
            //
            // order_type comes from the option prop because this same
            // component is reused by OrderSuper.vue and OrderCompany.vue
            // for super_parties / register_company_parties. Defaults to
            // 'trust' for backward compat.
            let party_class = current_object.option && current_object.option.trust_party_class;
            if (!party_class) return;
            let order_type = (current_object.option && current_object.option.order_type) || 'trust';

            amlApi.get('clients/picker', {
                order_type:  order_type,
                party_class: party_class,
            }).then(function (response) {
                current_object.aml_clients = response.data || [];
                current_object.aml_required = !!(response.meta && response.meta.required);
                current_object.party_risk_required = !!(response.meta && response.meta.party_risk_required);
                current_object.aml_picker_enabled = true;
            }).catch(function () {
                current_object.aml_clients = [];
                current_object.aml_required = false;
                current_object.party_risk_required = false;
                current_object.aml_picker_enabled = false;
            });
        },
        watch: {
            current_id: function () {
                let current_object = this;

                current_object.reset_cache_data();
                // sharing checkbox is per-party UI state — never carry it
                // from the previously edited party
                current_object.kyc_share_ui = false;

                console.log('UpdateTrustParty current_object.current_id change',current_object,current_object.current_id,$(current_object.$el.parentNode).offset().top,current_object.$el.parentNode.offsetTop);
                if (current_object.current_id && $(current_object.$el.parentNode).hasClass('scrolltop_container')) {
                    console.log('UpdateTrustParty scrollTop triggered',$(current_object.$el.parentNode).offset().top, current_object,current_object.$el);
                    // $('html, body').stop().animate({
                    //     scrollTop:$(current_object.$el.parentNode).offset().top - 20
                    // }, 550);
                    // $('html, body').scrollTop($(current_object.$el.parentNode).offset().top - 20);

                    $('html, body').stop().animate({
                        scrollTop:$(current_object.$el.parentNode).offset().top - 20
                    }, 550,'swing',function () {
                        $('html, body').scrollTop($(current_object.$el.parentNode).offset().top - 20);
                        console.log('UpdateTrustParty scrollTop 2',$(current_object.$el.parentNode).offset().top,$('html').scrollTop(),$('body').scrollTop(),$('#app').scrollTop());
                    });

                    console.log('UpdateTrustParty scrollTop 1',$(current_object.$el.parentNode).offset().top,$('html').scrollTop(),$('body').scrollTop(),$('#app').scrollTop())
                }

                current_object.message_option.message = '';
                current_object.message_option.message_type = '';
            }
        }
    }
</script>

<style scoped>
/* AML × ordering integration: subtle greyout for identity-verified
   fields locked because the party was sourced from an AmlClient. The
   form's base CSS gives both the inner <select> and its wrapping
   .select_container a white background with 1em border-radius — we
   need to grey both, otherwise the wrapper bleeds through at the
   rounded corners. */
.form_group_trust_party input[readonly],
.form_group_trust_party select:disabled,
.form_group_trust_party .select_container:has(select:disabled) {
    background-color: #f5f5f5 !important;
    color: #6a6a6a !important;
    cursor: not-allowed !important;
}

/* AML-verified pill rendered next to the per-class picker. Same
   shape and palette as .aml_customer_pill on OrderTrust.vue (kept
   local rather than shared since this component is also used
   outside the order forms — duplicating the styles is cheaper than
   plumbing a shared module). */
.form_group_trust_party .aml_party_pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
    margin-top: 0.5em;
    padding: 0.25em 0.7em;
    border-radius: 999px;
    font-size: 0.82em;
    font-weight: 600;
    line-height: 1.4;
}
.form_group_trust_party .aml_party_pill_clear { background: #dcfce7; color: #166534; }
.form_group_trust_party .aml_party_pill_warn  { background: #fef3c7; color: #92400e; }
.form_group_trust_party .aml_party_pill_icon  { font-size: 0.95em; }
.form_group_trust_party .aml_party_pill_date  { font-weight: 500; opacity: 0.85; }

/* Verified-required prompt shown when a mandatory-AML role has no
   client picked yet. Amber, advisory — pairs with the save-time block. */
.form_group_trust_party .aml_required_hint p {
    margin: 0.4em 0 0;
    padding: 0.5em 0.8em;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 0.5em;
    font-size: 0.82em;
    color: #92400e;
    line-height: 1.5;
}
</style>